import { Component, OnChanges, OnInit, Input, Output, EventEmitter, WritableSignal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TreeNode } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { TreeSelectModule } from 'primeng/treeselect';

export interface RunData {
  id: number | string;
  run_date: string | Date;
  relatedRunIds?: number[];
}

@Component({
    selector: 'app-prediction-selector',
    standalone: true,
    imports: [CommonModule, TreeSelectModule, FormsModule],
    templateUrl: './prediction-selector.component.html',
    styleUrl: './prediction-selector.component.scss'
})
export class PredictionSelectorComponent implements OnInit, OnChanges {
  @Input() runs: RunData[] = [];
  @Input() isLoading: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() placeholder: string = 'Select a prediction by run date';
  @Output() selectionChange = new EventEmitter<RunData>();

  treeData: WritableSignal<TreeNode[]> = signal([]);
  selectedNodeKey: string | null = null;

  ngOnInit(): void {
    this.buildRunOptions();
  }

  ngOnChanges(): void {
    this.buildRunOptions();
  }

  buildRunOptions(): void {
    if (!this.runs || this.runs.length === 0) {
      this.treeData.set([]);
      this.selectedNodeKey = null;
      return;
    }

    const treeNodes = this.createYearNodes(this.sortRunsByDateDescending(this.runs));
    this.treeData.set(treeNodes);

    if (this.selectedNodeKey) {
      const currentNode = this.findNodeByKey(treeNodes, this.selectedNodeKey);
      this.selectedNodeKey = currentNode?.key ?? null;
    }
  }

  private createYearNodes(runs: RunData[]): TreeNode[] {
    const groupedRuns = new Map<number, RunData[]>();

    runs.forEach((run) => {
      const year = new Date(run.run_date).getFullYear();
      const groupItems = groupedRuns.get(year) ?? [];
      groupItems.push(run);
      groupedRuns.set(year, groupItems);
    });

    return Array.from(groupedRuns.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([year, items]) => ({
        key: `year-${year}`,
        label: String(year),
        data: { year },
        expanded: false,
        selectable: false,
        children: items.map((run) => this.createRunNode(run))
      }));
  }

  private sortRunsByDateDescending(runs: RunData[]): RunData[] {
    return [...runs].sort((a, b) => 
      new Date(b.run_date).getTime() - new Date(a.run_date).getTime()
    );
  }

  private createRunNode(run: RunData): TreeNode {
    const runDate = new Date(run.run_date);
    const formattedDate = runDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    return {
      key: `run-${run.id}`,
      label: formattedDate,
      data: run,
      leaf: true,
      selectable: true,
    };
  }

  onNodeSelect(event: any): void {
    const selectedNode = event.node as TreeNode | undefined;
    if (selectedNode?.data?.id !== undefined) {
      this.selectionChange.emit(selectedNode.data as RunData);
    }
  }

  clearSelection(): void {
    this.selectedNodeKey = null;
  }

  getSelectedDateLabel(): string {
    if (!this.selectedNodeKey) {
      return '';
    }

    const node = this.findNodeByKey(this.treeData(), this.selectedNodeKey);
    return node?.label || '';
  }

  private findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (node.key === key) {
        return node;
      }

      const childNode = this.findNodeByKey(node.children || [], key);
      if (childNode) {
        return childNode;
      }
    }

    return null;
  }
}
